import { SMALL_CASE_WORK_ORDER } from "components/Common/constants";
import React from "react";

const PrivacyPolicy = () => {
  // Update this date when you publish the policy
  const LAST_UPDATED_DATE = "November 28, 2025";

  return (
    <div className="privacy-policy">
      <div className="policy-header text-white">
        <h1>Privacy & Data Policy</h1>
        <h2>Falcon Kitchen's Service Management</h2>
        <p className="last-updated">Last Updated: {LAST_UPDATED_DATE}</p>
      </div>

      <div className="policy-content">
        <section className="policy-section">
          <h3>Introduction</h3>
          <p>
            Falcon Kitchens provides digital tools to help customers request
            services, technicians complete jobs, and administrators manage
            operations. This Privacy Policy explains how we collect, use, and
            protect your information across our web portal and mobile
            applications. We are committed to safeguarding your data and
            maintaining transparency in how it is handled.
          </p>
        </section>

        <section className="policy-section">
          <h3>Data Collected and Stored</h3>
          <p>
            We collect only the information necessary to deliver installation,
            repair, and maintenance services through the Falcon Admin Portal,
            Falcon Customer App, and Falcon Technician App. This includes:
          </p>
          <ul>
            <li>
              <strong>Customer Personal Data:</strong> Name, phone number, email
              address, residential or business address, and service/{SMALL_CASE_WORK_ORDER} history.
            </li>
            <li>
              <strong>Technician Personal Data:</strong> Name, email address,
              and system-generated login credentials used exclusively for
              authentication and {SMALL_CASE_WORK_ORDER} assignment.
            </li>
            <li>
              <strong>Location Data:</strong> Customer location (approximate or
              precise) used to help technicians reach the correct service
              address.
            </li>
            <li>
              <strong>Service & Operational Data:</strong> {SMALL_CASE_WORK_ORDER} details, repair
              notes, activity logs, timestamps, status updates, signatures and
              any photos or videos captured for diagnostic or documentation
              purposes.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h3>Purpose of Collection and Use</h3>
          <p>
            The collected data is used solely to operate, manage, and improve
            Falcon Kitchens’ service delivery, technician workflow, and overall
            system performance. Specifically, the data is used for:
          </p>

          <ul>
            <li>
              <strong>Authentication & Account Management:</strong>
              To securely authenticate technician logins, manage user accounts,
              verify identities, and maintain accurate system access records.
            </li>

            <li>
              <strong>Service Delivery & Scheduling:</strong>
              To process customer service requests, schedule and assign jobs to
              technicians based on availability and location, and accurately
              identify the customer’s service address.
            </li>

            <li>
              <strong>Diagnostics & Service Quality:</strong>
              To capture {SMALL_CASE_WORK_ORDER} notes, document equipment conditions, maintain
              complete service history, and use photos/videos for
              troubleshooting, verification, and quality assurance.
            </li>

            <li>
              <strong>Safety, Security & Compliance:</strong>
              To ensure secure access to the system, maintain activity logs for
              auditing and accountability, and comply with legal or regulatory
              obligations when required.
            </li>
          </ul>
        </section>

        <section className="policy-section">
          <h3>Data Usage and Sharing</h3>
          <p>
            All collected data is used internally within authorized Falcon
            Kitchens teams for {SMALL_CASE_WORK_ORDER} management, customer support, scheduling,
            billing, and service quality improvements. We do not sell, rent, or
            share your data with external third parties unless required by law.
          </p>
          <p>
            The information may also be used for internal analytics, operational
            decisions, sending essential service notifications, emails or
            updates, provide customer support, and manage daily operations.
          </p>
        </section>

        <section className="policy-section">
          <h3>Third-Party Services</h3>
          <p>
            The application may use third-party services such as Google Maps,
            Firebase, push notification providers, analytics tools, or cloud
            storage. These services may collect limited information required for
            their functionality. Falcon Kitchens ensures all third-party
            integrations follow industry-standard security and privacy
            practices.
          </p>
        </section>

        <section className="policy-section">
          <h3>User Tracking</h3>
          <p>
            Falcon Kitchens does not track users across other apps or websites.
            We do not use advertising SDKs or third-party tracking technologies.
            No data is collected for targeted advertising or cross-platform
            tracking.
          </p>
        </section>

        <section className="policy-section">
          <h3>Data Security and Confidentiality</h3>
          <p>
            All data exchanges are encrypted using industry-standard protocols
            (HTTPS/SSL). Access to the system is restricted based on user roles
            and enforced through secure authentication measures.
          </p>
        </section>

        <section className="policy-section">
          <h3>Data Retention and Deletion</h3>
          <p>
            Records are retained as per Falcon Kitchens business and legal
            requirements and securely deleted or archived when no longer
            required for operational purposes.
          </p>
        </section>

        <section className="policy-section">
          <h3>Policy Updates</h3>
          <p>
            This policy may be updated periodically to reflect regulatory or
            system changes. Updated versions will be made available within the
            application and on our website.
          </p>
        </section>

        <section className="policy-section">
          <h3>Contact</h3>
          <p>
            For any questions or concerns regarding this policy or your data,
            please contact the system administrator or Falcon Kitchens IT
            support team.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
